import { DataSourceStatus } from '@/lib/graphql/generated/graphql';

/**
 * Forme minimale requise pour réconcilier un datasource : son identifiant et son
 * statut. Volontairement plus permissif que l'union GraphQL `DealDataSource`,
 * dont certains membres n'exposent pas `status` — la réconciliation n'a besoin
 * de rien d'autre, et rester structurel garde la fonction testable sans monter
 * un objet GraphQL complet.
 */
export type ReconcilableDatasource = {
  id: string;
  status?: DataSourceStatus | null;
};

export type DatasourceReconciliation<T> = {
  /** Imports aboutis : ouvrir le rapport d'import / résoudre le toast en succès. */
  processed: T[];
  /** Imports en échec : nettoyer le toast "en cours" et afficher l'erreur. */
  failed: T[];
};

/**
 * Détermine, parmi les datasources dont on attend encore la fin, lesquels ont
 * atteint un état terminal dans l'état serveur fraîchement relu.
 *
 * Le signal SSE ne porte pas l'identifiant du datasource. Dériver l'identité
 * de l'état plutôt que de la recevoir dans l'événement rend la résolution
 * insensible aux pertes de signal (coalescing sous backpressure, `SYNC` de
 * reconnexion) : au pire elle a lieu un aller-retour plus tard.
 *
 * Seuls `processed` et `error` sont considérés comme terminaux ; tout autre
 * statut (`created`, `pending`, `imported`) laisse l'attente en cours. Un
 * identifiant attendu absent de `dataSources` est ignoré plutôt que traité comme
 * un échec : la liste des datasources peut être partielle le temps qu'un refetch
 * aboutisse.
 *
 * Fonction pure : c'est l'appelant qui résout les toasts, et cette résolution
 * retire l'identifiant des attentes, ce qui rend un second appel sans effet.
 */
export function reconcilePendingDatasources<T extends ReconcilableDatasource>({
  pendingIds,
  dataSources,
}: {
  pendingIds: readonly string[];
  dataSources: readonly T[];
}): DatasourceReconciliation<T> {
  const processed: T[] = [];
  const failed: T[] = [];

  if (pendingIds.length === 0) return { processed, failed };

  const pending = new Set(pendingIds);

  for (const dataSource of dataSources) {
    if (!pending.has(dataSource.id)) continue;
    if (dataSource.status === DataSourceStatus.Processed) {
      processed.push(dataSource);
    } else if (dataSource.status === DataSourceStatus.Error) {
      failed.push(dataSource);
    }
  }

  return { processed, failed };
}
